import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatINR, formatDate, todayISO } from "@/lib/format";
import { printSection } from "@/lib/print";
import { FileText, Printer, ClipboardList, BookOpen } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Brand grouping                                                      */
/* ------------------------------------------------------------------ */
const BRANDS = [
  { brand: "MAHINDRA", kw: ["mahindra"] },
  { brand: "MG", kw: ["mg"] },
  { brand: "HYUNDAI", kw: ["hyundai"] },
  { brand: "MARUTI SUZUKI", kw: ["maruti", "suzuki"] },
  { brand: "TOYOTA", kw: ["toyota"] },
  { brand: "KIA", kw: ["kia"] },
  { brand: "TATA", kw: ["tata"] },
  { brand: "RENAULT", kw: ["renault"] },
  { brand: "VOLKSWAGEN", kw: ["volkswagen", "vw"] },
  { brand: "LAND ROVER", kw: ["land rover", "range rover"] },
  { brand: "JEEP", kw: ["jeep"] },
  { brand: "HONDA", kw: ["honda"] },
  { brand: "ISUZU/D-MAX", kw: ["isuzu", "d-max", "dmax"] },
  { brand: "MERCEDES", kw: ["mercedes", "benz"] },
  { brand: "FORD", kw: ["ford"] },
];

function detectBrand(model) {
  const m = (model || "").toLowerCase();
  for (const b of BRANDS) {
    for (const k of b.kw) {
      const re = new RegExp(`\\b${k.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`, "i");
      if (re.test(m)) return b.brand;
    }
  }
  return "OTHER";
}

const statusLabel = {
  available: "In Stock",
  booked: "Booked",
  sold: "Sold",
  delivered: "Delivered",
};

function shiftDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

const stockRanges = [
  { key: "today", label: "Today" },
  { key: "10d", label: "Last 10 Days" },
  { key: "mtd", label: "1 Month / MTD" },
  { key: "custom", label: "Custom Date Range" },
];

function rangeBounds(key, start, end) {
  const today = todayISO();
  if (key === "today") return [today, today];
  if (key === "10d") return [shiftDays(today, -9), today];
  if (key === "mtd") return [today.slice(0, 8) + "01", today];
  return [start, end];
}

/* ================================================================== */
export default function Reports() {
  const [tab, setTab] = useState("stock");

  return (
    <div className="a1-fade">
      <div className="no-print">
        <h1 className="font-head font-black text-3xl sm:text-4xl tracking-tight mb-1 flex items-center gap-2">
          <FileText className="w-8 h-8 text-[#1E3A8A]" /> Reports
        </h1>
        <p className="text-slate-500 font-semibold mb-6">Printable stock register &amp; daily cashier day book.</p>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            data-testid="tab-stock"
            onClick={() => setTab("stock")}
            className={`px-5 py-3 rounded-xl font-bold flex items-center gap-2 transition-colors ${tab === "stock" ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            <ClipboardList className="w-5 h-5" /> Stock Register Report
          </button>
          <button
            data-testid="tab-daybook"
            onClick={() => setTab("daybook")}
            className={`px-5 py-3 rounded-xl font-bold flex items-center gap-2 transition-colors ${tab === "daybook" ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
          >
            <BookOpen className="w-5 h-5" /> Cashier Day Book Report
          </button>
        </div>
      </div>

      {tab === "stock" ? <StockRegister /> : <DayBook />}
    </div>
  );
}

/* ================================================================== */
/* TAB A — STOCK REGISTER                                              */
/* ================================================================== */
function StockRegister() {
  const [vehicles, setVehicles] = useState([]);
  const [range, setRange] = useState("mtd");
  const [start, setStart] = useState(todayISO().slice(0, 8) + "01");
  const [end, setEnd] = useState(todayISO());

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const today = todayISO();
  const yday = shiftDays(today, -1);
  const [s, e] = rangeBounds(range, start, end);
  const inRange = (d) => d && d.slice(0, 10) >= s && d.slice(0, 10) <= e;

  // Live stock = available + booked
  const live = vehicles.filter((v) => ["available", "booked"].includes(v.status));

  // Group by brand in defined order
  const order = [...BRANDS.map((b) => b.brand), "OTHER"];
  const grouped = {};
  live.forEach((v) => {
    const b = detectBrand(v.model);
    (grouped[b] = grouped[b] || []).push(v);
  });
  const groups = order.filter((b) => grouped[b]?.length).map((b) => ({ brand: b, cars: grouped[b] }));

  let serial = 0;

  const workKw = ["tinker", "painter", "mech", "wash", "water", "police", "on the way", "nayab", "jakier", "gopi", "rafi", "kadapa"];
  const isOutside = (v) => workKw.some((k) => (v.notes || "").toLowerCase().includes(k));

  const summary = {
    yesterdaySold: vehicles.filter((v) => ["sold", "delivered"].includes(v.status) && inRangeDay(v.sold_at, yday)).length,
    todaySales: vehicles.filter((v) => ["sold", "delivered"].includes(v.status) && inRangeDay(v.sold_at, today)).length,
    todayPurchase: vehicles.filter((v) => inRangeDay(v.inward_date, today)).length,
    todayBooking: vehicles.filter((v) => (v.advances || []).some((a) => a.direction === "in" && inRangeDay(a.date, today))).length,
    yesterdayReturns: vehicles.filter((v) => (v.advances || []).some((a) => a.direction === "out" && inRangeDay(a.date, yday))).length,
    liveStock: live.length,
    inside: live.filter((v) => !isOutside(v)).length,
    outside: live.filter((v) => isOutside(v)).length,
    periodPurchase: vehicles.filter((v) => inRange(v.inward_date)).length,
    periodSales: vehicles.filter((v) => ["sold", "delivered"].includes(v.status) && inRange(v.sold_at)).length,
  };

  return (
    <div>
      <div className="no-print flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {stockRanges.map((r) => (
            <button
              key={r.key}
              data-testid={`stock-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-4 py-2.5 rounded-xl font-bold whitespace-nowrap transition-colors ${range === r.key ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <button data-testid="print-stock" onClick={() => printSection("stock")} className="a1-btn a1-btn-action !h-12 !text-base">
          <Printer className="w-5 h-5" /> Print Stock Report
        </button>
      </div>

      {range === "custom" && (
        <div className="no-print flex gap-4 flex-wrap mb-4 items-end">
          <div>
            <label className="a1-label">From</label>
            <input data-testid="stock-start" type="date" className="a1-input" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className="a1-label">To</label>
            <input data-testid="stock-end" type="date" className="a1-input" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
      )}

      <div id="print-stock" className="a1-card p-6" data-testid="stock-report">
        <div className="flex items-center gap-3 mb-4" style={{ borderBottom: "3px solid #1E3A8A", paddingBottom: 12 }}>
          <img src="/logo.png" alt="A1" style={{ width: 48, height: 48, borderRadius: "50%" }} />
          <div>
            <div className="font-head font-black text-2xl" style={{ color: "#1E3A8A" }}>A1 CARS AGENCY</div>
            <div className="text-sm font-bold text-slate-500">Stock Register • {formatDate(s)} – {formatDate(e)}</div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#1E3A8A] text-white text-left">
                <th className="p-2 font-bold">S.No</th>
                <th className="p-2 font-bold">CAR NAME</th>
                <th className="p-2 font-bold">MODEL (Year)</th>
                <th className="p-2 font-bold">VEHICLE NO</th>
                <th className="p-2 font-bold">COLOUR</th>
                <th className="p-2 font-bold">STATUS / P&amp;S</th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-slate-400 font-semibold">No live stock.</td></tr>
              )}
              {groups.map((g) => {
                const startSerial = serial;
                serial += g.cars.length;
                return <FragmentGroup key={g.brand} g={g} startSerial={startSerial} />;
              })}
            </tbody>
          </table>
        </div>

        {/* Footer ledger summary */}
        <div className="mt-6" style={{ borderTop: "2px solid #0F172A", paddingTop: 12 }}>
          <div className="font-head font-extrabold text-lg tracking-tight mb-3">Ledger Summary</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Yesterday Sold Out" value={summary.yesterdaySold} />
            <Stat label="Today Sales" value={summary.todaySales} />
            <Stat label="Today Purchase" value={summary.todayPurchase} />
            <Stat label="Today Placement / Booking" value={summary.todayBooking} />
            <Stat label="Yesterday Sales/Purchase Returns" value={summary.yesterdayReturns} />
            <Stat label="Current Live Stock" value={summary.liveStock} accent="#1E3A8A" />
            <Stat label="Inside Parking" value={summary.inside} />
            <Stat label="Outside / Work Parking" value={summary.outside} accent="#EA580C" />
          </div>
        </div>
      </div>
    </div>
  );
}

function inRangeDay(d, day) {
  return d && d.slice(0, 10) === day;
}

function FragmentGroup({ g, startSerial }) {
  return (
    <>
      <tr>
        <td colSpan={6} className="bg-slate-100 p-2 font-head font-black text-[#1E3A8A] tracking-wide">{g.brand}</td>
      </tr>
      {g.cars.map((v, i) => (
        <tr key={v.id} className="border-t border-slate-200" data-testid={`stock-row-${v.id}`}>
          <td className="p-2 num font-bold">{startSerial + i + 1}</td>
          <td className="p-2 font-semibold">{v.model}</td>
          <td className="p-2 num">{v.year || "—"}</td>
          <td className="p-2 num font-bold">{v.reg_no}</td>
          <td className="p-2">{v.color || "—"}</td>
          <td className="p-2 font-semibold text-slate-600">
            {statusLabel[v.status] || v.status}{v.notes ? ` • ${v.notes}` : ""}
          </td>
        </tr>
      ))}
    </>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="num font-head font-black text-2xl" style={{ color: accent || "#0F172A" }}>{value}</div>
      <div className="text-xs font-bold text-slate-500 leading-tight mt-0.5">{label}</div>
    </div>
  );
}

/* ================================================================== */
/* TAB B — CASHIER DAY BOOK                                            */
/* ================================================================== */
function DayBook() {
  const [date, setDate] = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);

  const load = useCallback(() => {
    api.get("/cashier/summary", { params: { date } }).then((r) => setSummary(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  if (!summary) return <div className="text-slate-400 font-semibold">Loading…</div>;

  const onDay = (d) => d && d.slice(0, 10) === date;
  const vmap = Object.fromEntries(vehicles.map((v) => [v.id, v]));

  // Section 1 — Sales completed on this date
  const sales = vehicles
    .filter((v) => ["sold", "delivered"].includes(v.status) && onDay(v.sold_at))
    .map((v) => {
      const cash = (v.payments || []).filter((p) => p.mode === "cash").reduce((a, p) => a + p.amount, 0)
        + (v.advances || []).filter((a) => a.direction === "in" && a.mode === "cash").reduce((a, x) => a + x.amount, 0);
      const bank = (v.payments || []).filter((p) => p.mode === "upi" || p.mode === "rtgs").reduce((a, p) => a + p.amount, 0)
        + (v.advances || []).filter((a) => a.direction === "in" && a.mode === "upi").reduce((a, x) => a + x.amount, 0);
      return {
        id: v.id, car: `${v.model} (${v.year || "—"})`, reg: v.reg_no, buyer: v.customer_name || "—",
        price: v.selling_price || 0, cash, bank, balance: v.totals?.customer_pending || 0,
      };
    });

  // Section 2 — Sales advances (token) collected on this date
  const advances = summary.transactions
    .filter((t) => t.direction === "in" && (t.narration || "").toLowerCase().startsWith("token advance"))
    .map((t) => {
      const v = vmap[t.ref_id] || {};
      return { id: t.id, particulars: t.narration, customer: v.customer_name || "—", mode: t.mode, cr: t.amount };
    });

  // Section 3 — Balance clearances collected today on PRE-EXISTING bookings/sales.
  // A "clearance" = a Sale-payment inflow dated today for a car that was booked
  // (advance-in) OR sold on a PRIOR day. Same-day fresh sales stay only in Section 1.
  const soldTodayIds = new Set(sales.map((s) => s.id));
  const isPreExisting = (vid) => {
    const v = vmap[vid];
    if (!v) return false;
    const bookedPrior = (v.advances || []).some((a) => a.direction === "in" && a.date && a.date.slice(0, 10) < date);
    const soldPrior = v.sold_at && v.sold_at.slice(0, 10) < date;
    return bookedPrior || soldPrior;
  };
  const pending = summary.transactions
    .filter((t) => t.direction === "in" && (t.narration || "").toLowerCase().startsWith("sale payment")
      && (isPreExisting(t.ref_id) || !soldTodayIds.has(t.ref_id)))
    .map((t) => {
      const v = vmap[t.ref_id] || {};
      const src = t.kind === "cash" ? "Cash" : (t.mode === "upi" ? "UPI" : "Bank ICICI");
      return { id: t.id, car: `${v.model || "—"} (${v.reg_no || ""})`, customer: v.customer_name || "—", mode: src, utr: t.utr || "", cr: t.amount };
    });

  // Section 4 — all money given out today (expenses, staff, fuel, purchases, settlements)
  const expenses = summary.transactions
    .filter((t) => t.direction === "out")
    .map((t) => ({ id: t.id, source: t.kind === "cash" ? "Cash" : "Bank", purpose: t.narration || "Cash given out", amount: t.amount }));
  const totalOut = expenses.reduce((a, e) => a + e.amount, 0);

  return (
    <div>
      <div className="no-print flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <label className="a1-label">Date</label>
          <input data-testid="daybook-date" type="date" className="a1-input" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <button data-testid="print-daybook" onClick={() => printSection("daybook")} className="a1-btn a1-btn-action !h-12 !text-base">
          <Printer className="w-5 h-5" /> Print Day Book
        </button>
      </div>

      <div id="print-daybook" className="a1-card p-6" data-testid="daybook-report">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3" style={{ borderBottom: "3px solid #1E3A8A", paddingBottom: 12 }}>
          <img src="/logo.png" alt="A1" style={{ width: 48, height: 48, borderRadius: "50%" }} />
          <div>
            <div className="font-head font-black text-2xl" style={{ color: "#1E3A8A" }}>A1 CARS DAY BOOK</div>
            <div className="text-sm font-bold text-slate-500">Date: {formatDate(date)}</div>
          </div>
        </div>

        {/* Opening balances sub-header */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5 text-sm font-bold">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">OB ICICI BANK : <span className="num text-[#2563EB]">{formatINR(summary.opening_bank)}</span></div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">OB INDUS 786 : <span className="num text-[#2563EB]">{formatINR(0)}</span></div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">OB CASH : <span className="num text-[#1E3A8A]">{formatINR(summary.opening_cash)}</span></div>
        </div>

        {/* Section 1 — Sales */}
        <SectionHead n="1" title="SALES" />
        <div className="overflow-x-auto mb-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#1E3A8A] text-white text-left">
                <th className="p-2 font-bold">S.No</th>
                <th className="p-2 font-bold">Car (Year) • Reg No</th>
                <th className="p-2 font-bold">Buyer</th>
                <th className="p-2 font-bold text-right">Full Price</th>
                <th className="p-2 font-bold text-right">Cash</th>
                <th className="p-2 font-bold text-right">Bank / ICICI</th>
                <th className="p-2 font-bold text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {sales.length === 0 && <tr><td colSpan={7} className="p-3 text-slate-400 font-semibold">No sales on this date.</td></tr>}
              {sales.map((s, i) => (
                <tr key={s.id} className="border-t border-slate-200">
                  <td className="p-2 num font-bold">{i + 1}</td>
                  <td className="p-2 font-semibold">{s.car}<div className="num text-xs text-slate-500">{s.reg}</div></td>
                  <td className="p-2">{s.buyer}</td>
                  <td className="p-2 num font-bold text-right">{formatINR(s.price)}</td>
                  <td className="p-2 num text-right text-[#16A34A]">{formatINR(s.cash)}</td>
                  <td className="p-2 num text-right text-[#2563EB]">{formatINR(s.bank)}</td>
                  <td className="p-2 num text-right" style={{ color: s.balance > 0 ? "#EA580C" : "#16A34A" }}>{formatINR(s.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Section 2 — Sales Advance */}
        <SectionHead n="2" title="SALES ADVANCE (Tokens collected today)" />
        <div className="overflow-x-auto mb-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-700 text-white text-left">
                <th className="p-2 font-bold">Particulars</th>
                <th className="p-2 font-bold">Customer</th>
                <th className="p-2 font-bold text-right">DR</th>
                <th className="p-2 font-bold text-right">CR</th>
              </tr>
            </thead>
            <tbody>
              {advances.length === 0 && <tr><td colSpan={4} className="p-3 text-slate-400 font-semibold">No advances collected today.</td></tr>}
              {advances.map((a) => (
                <tr key={a.id} className="border-t border-slate-200">
                  <td className="p-2 font-semibold">{a.particulars} <span className="uppercase text-xs text-slate-400">({a.mode})</span></td>
                  <td className="p-2">{a.customer}</td>
                  <td className="p-2 num text-right text-slate-400">—</td>
                  <td className="p-2 num font-bold text-right text-[#16A34A]">{formatINR(a.cr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Section 3 — Sales Pending Balance */}
        <SectionHead n="3" title="SALES PENDING BALANCE (Clearances today)" />
        <div className="overflow-x-auto mb-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-700 text-white text-left">
                <th className="p-2 font-bold">Car Name &amp; Reg No</th>
                <th className="p-2 font-bold">Customer</th>
                <th className="p-2 font-bold">Payment Mode / Ref</th>
                <th className="p-2 font-bold text-right">CR</th>
              </tr>
            </thead>
            <tbody>
              {pending.length === 0 && <tr><td colSpan={4} className="p-3 text-slate-400 font-semibold">No pending clearances today.</td></tr>}
              {pending.map((p) => (
                <tr key={p.id} className="border-t border-slate-200" data-testid={`pending-row-${p.id}`}>
                  <td className="p-2 font-semibold">{p.car}</td>
                  <td className="p-2">{p.customer}</td>
                  <td className="p-2 font-semibold"><span className="uppercase">{p.mode}</span>{p.utr ? ` • ${p.utr}` : ""}</td>
                  <td className="p-2 num font-bold text-right text-[#16A34A]">{formatINR(p.cr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Section 4 — Money given out / expenses */}
        <SectionHead n="4" title="MONEY GIVEN OUT / EXPENSES" />
        <div className="overflow-x-auto mb-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-700 text-white text-left">
                <th className="p-2 font-bold">S.No</th>
                <th className="p-2 font-bold">Outflow Source</th>
                <th className="p-2 font-bold">Purpose / Narration</th>
                <th className="p-2 font-bold text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 && <tr><td colSpan={4} className="p-3 text-slate-400 font-semibold">No money given out today.</td></tr>}
              {expenses.map((x, i) => (
                <tr key={x.id} className="border-t border-slate-200" data-testid={`expense-row-${x.id}`}>
                  <td className="p-2 num font-bold">{i + 1}</td>
                  <td className="p-2 font-semibold"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${x.source === "Cash" ? "bg-blue-100 text-[#1E3A8A]" : "bg-indigo-100 text-[#2563EB]"}`}>{x.source}</span></td>
                  <td className="p-2 font-semibold">{x.purpose}</td>
                  <td className="p-2 num font-bold text-right text-[#EA580C]">- {formatINR(x.amount)}</td>
                </tr>
              ))}
              {expenses.length > 0 && (
                <tr style={{ borderTop: "2px solid #0F172A" }} data-testid="total-outflows-row">
                  <td className="p-2 font-black" colSpan={3}>TOTAL OUTFLOWS</td>
                  <td className="p-2 num font-head font-black text-right text-[#EA580C]">- {formatINR(totalOut)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Section 5 — Closing summary */}
        <SectionHead n="5" title="CLOSING SUMMARY" />
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mb-3 text-sm font-semibold text-slate-600" data-testid="closing-formula">
          Closing = (Opening + Daily Inflows) − Daily Outflows &nbsp;•&nbsp;
          Cash: {formatINR(summary.opening_cash)} + {formatINR(summary.cash_in)} − {formatINR(summary.cash_out)} = <span className="num font-black text-[#1E3A8A]">{formatINR(summary.closing_cash)}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <Stat label="Today Closing Cash" value={formatINR(summary.closing_cash)} accent="#1E3A8A" isText />
          <Stat label="Today ICICI Bank Closing" value={formatINR(summary.closing_bank)} accent="#2563EB" isText />
          <Stat label="Today Indus 786 Closing" value={formatINR(0)} accent="#2563EB" isText />
        </div>
        <div className="rounded-xl border border-dashed border-slate-300 p-3 text-sm text-slate-600 font-semibold mb-6">
          Handover Narration: Cash &amp; bank balances as above handed over and verified at day close.
        </div>
        <div className="flex justify-between mt-10 text-sm font-bold">
          <div style={{ borderTop: "1px solid #0F172A", paddingTop: 6, width: "45%", textAlign: "center" }}>Cashier Signature</div>
          <div style={{ borderTop: "1px solid #0F172A", paddingTop: 6, width: "45%", textAlign: "center" }}>Manager Sign-off</div>
        </div>
      </div>
    </div>
  );
}

function SectionHead({ n, title }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <span className="w-6 h-6 rounded-full bg-[#EA580C] text-white text-xs font-black flex items-center justify-center">{n}</span>
      <span className="font-head font-extrabold text-lg tracking-tight">{title}</span>
    </div>
  );
}
